import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TableFooter,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_BASE_URL } from '../constants/config';
import { getCategoryOptions } from '../constants/categories';

type InvoiceStatusType = 'PENDING_APPROVAL' | 'APPROVED' | 'DELIVERED';

interface Item {
  id: number;
  name: string;
  quantityUnit: string;
  pricePerUnit: number | null;
  addAmountToBillInPOS: boolean;
  manufacturedInFactory: boolean;
  category: string;
  consumable: number;
  manufacturingPricePerUnit: number | null;
  avgWeightPerUnit: number | null;
}

interface InvoiceLine {
  itemName: string;
  quantity: number;
  pricePerUnit: number;
  quantityUnit: string;
  total: number;
  discount: number;
}

interface SelectedItem {
  name: string;
  pricePerUnit: number;
  quantityUnit: string;
  category: string;
  addAmountToBillInPOS: boolean;
  manufacturingPricePerUnit: number | null;
  avgWeightPerUnit: number | null;
}

interface InvoiceListItem {
  id: number;
  createdDate: string;
  shopName?: string;
  username: string;
  status: InvoiceStatusType;
  orderType?: string;
  paymentMethod?: string;
}

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const isAdministrator = role === 'Administrator';

  const [items, setItems] = useState<Item[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [pieceCount, setPieceCount] = useState(1);
  const [currentLines, setCurrentLines] = useState<InvoiceLine[]>([]);
  const [selectedItem, setSelectedItem] = useState<SelectedItem>({
    name: '',
    pricePerUnit: 0,
    quantityUnit: '',
    category: '',
    addAmountToBillInPOS: false,
    manufacturingPricePerUnit: null,
    avgWeightPerUnit: null
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [orderType, setOrderType] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [contact, setContact] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [ntnNo, setNtnNo] = useState('');
  const [company, setCompany] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyNtnNo, setCompanyNtnNo] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [deliveryCharges, setDeliveryCharges] = useState<number>(0);
  const [applyGst, setApplyGst] = useState<boolean>(false);
  const [applyGstOnDelivery, setApplyGstOnDelivery] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [invoiceList, setInvoiceList] = useState<InvoiceListItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [deleteInProgressId, setDeleteInProgressId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const paymentMethodOptions = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CARD', label: 'Card' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' }
  ];

  const orderTypeOptions = [
    { value: 'WALKIN', label: 'Walkin Customer' },
    { value: 'WEBSITE', label: 'Website' },
    { value: 'PHONE', label: 'Phone Order' },
    { value: 'FOODPANDA', label: 'Food Panda' }
  ];

  useEffect(() => {
    if (token) {
      fetchItems();
    }
  }, [token]);

  const fetchItems = async () => {
    const shopName = localStorage.getItem('shopName');
    try {
      const url = shopName
        ? `${API_BASE_URL}/inventory-transactions/status/shop/${encodeURIComponent(shopName)}`
        : `${API_BASE_URL}/inventory-transactions/status`;
      const response = await axios.get(url, { headers: { Authorization: token } });
      const transformedItems = response.data.map((item: any) => ({
        id: item.id || 0,
        name: item.itemName,
        quantityUnit: item.quantityUnit,
        pricePerUnit: item.pricePerUnit || 0,
        addAmountToBillInPOS: item.addAmountToBillInPOS ?? true,
        manufacturedInFactory: item.manufacturedInFactory ?? false,
        category: item.category,
        consumable: item.consumable ?? 0,
        manufacturingPricePerUnit: item.manufacturingPricePerUnit || 0,
        avgWeightPerUnit: item.avgWeightPerUnit || 0
      }));
      setItems(transformedItems);
    } catch (err: any) {
      if (err.response?.status === 401) navigate('/login');
      else console.error('Error fetching items:', err);
    }
  };

  const handleAddToInvoice = () => {
    if (!selectedItem.name || quantity <= 0) return;
    const itemDetails = items.find(item => item.name === selectedItem.name);
    if (!itemDetails) return;

    const pricePerUnit = itemDetails.addAmountToBillInPOS ? (itemDetails.pricePerUnit ?? 0) : 0;
    const existingIndex = currentLines.findIndex(line => line.itemName === selectedItem.name);

    if (existingIndex >= 0) {
      const updated = [...currentLines];
      const existing = updated[existingIndex];
      const newQty = existing.quantity + quantity;
      updated[existingIndex] = {
        ...existing,
        quantity: newQty,
        pricePerUnit,
        total: pricePerUnit * newQty * (1 - existing.discount / 100)
      };
      setCurrentLines(updated);
    } else {
      setCurrentLines([
        ...currentLines,
        {
          itemName: selectedItem.name,
          quantity,
          pricePerUnit,
          quantityUnit: selectedItem.quantityUnit,
          total: pricePerUnit * quantity,
          discount: 0
        }
      ]);
    }
    setSelectedItem({ name: '', pricePerUnit: 0, quantityUnit: '', category: '', addAmountToBillInPOS: false, manufacturingPricePerUnit: null, avgWeightPerUnit: null });
    setQuantity(1);
  };

  const handleRemoveFromInvoice = (index: number) => {
    setCurrentLines(prev => prev.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => currentLines.reduce((sum, item) => sum + item.total, 0);

  const handleLineDiscountChange = (index: number, newDiscount: number) => {
    const clamped = Math.min(Math.max(newDiscount, 0), 50);
    setCurrentLines(prev => {
      const next = [...prev];
      const item = next[index];
      item.discount = clamped;
      item.total = item.pricePerUnit * item.quantity * (1 - clamped / 100);
      return next;
    });
  };

  const openListDialog = async () => {
    setListDialogOpen(true);
    setListLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/invoices`, { headers: { Authorization: token } });
      const list = res.data.map((inv: any) => ({
        id: inv.id,
        createdDate: inv.createdDate,
        shopName: inv.shopName,
        username: inv.username,
        status: inv.status,
        orderType: inv.orderType,
        paymentMethod: inv.paymentMethod
      }));
      list.sort((a: InvoiceListItem, b: InvoiceListItem) => {
        const da = a.createdDate || '';
        const db = b.createdDate || '';
        return da < db ? 1 : da > db ? -1 : 0;
      });
      setInvoiceList(list);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load invoices', severity: 'error' });
    } finally {
      setListLoading(false);
    }
  };

  const handleMarkApproved = async (id: number) => {
    setStatusUpdatingId(id);
    try {
      await axios.patch(`${API_BASE_URL}/invoices/${id}/status?status=APPROVED`, {}, { headers: { Authorization: token } });
      setInvoiceList(prev => prev.map(inv => inv.id === id ? { ...inv, status: 'APPROVED' as InvoiceStatusType } : inv));
      setSnackbar({ open: true, message: 'Invoice marked APPROVED', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to update status', severity: 'error' });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleMarkDelivered = async (id: number) => {
    setStatusUpdatingId(id);
    try {
      await axios.patch(`${API_BASE_URL}/invoices/${id}/status?status=DELIVERED`, {}, { headers: { Authorization: token } });
      setInvoiceList(prev => prev.map(inv => inv.id === id ? { ...inv, status: 'DELIVERED' as InvoiceStatusType } : inv));
      setSnackbar({ open: true, message: 'Invoice marked DELIVERED', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to update status', severity: 'error' });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDeleteInvoice = async (id: number) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    setDeleteInProgressId(id);
    try {
      await axios.delete(`${API_BASE_URL}/invoices/${id}`, { headers: { Authorization: token } });
      setInvoiceList(prev => prev.filter(inv => inv.id !== id));
      setSnackbar({ open: true, message: 'Invoice deleted', severity: 'success' });
      if (editingInvoiceId === id) {
        handleNewInvoice();
      }
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to delete invoice', severity: 'error' });
    } finally {
      setDeleteInProgressId(null);
    }
  };

  const handleEditInvoice = async (id: number) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/invoices/${id}`, { headers: { Authorization: token } });
      const inv = res.data;
      if (inv.status !== 'PENDING_APPROVAL') {
        setSnackbar({ open: true, message: 'Only PENDING_APPROVAL invoices can be edited', severity: 'error' });
        return;
      }
      setEditingInvoiceId(inv.id);
      setPaymentMethod(inv.paymentMethod || '');
      setOrderType(inv.orderType || '');
      setCustomerName(inv.customerName || '');
      setContact(inv.contact || '');
      setMobileNo(inv.mobileNo || '');
      setNtnNo(inv.ntnNo || '');
      setCompany(inv.company || '');
      setCompanyAddress(inv.companyAddress || '');
      setCompanyNtnNo(inv.companyNtnNo || '');
      setPaymentDueDate(inv.paymentDueDate || '');
      setInvoiceDate(inv.createdDate || '');
      setDeliveryCharges(inv.deliveryCharges ?? 0);
      setApplyGst(inv.applyGst ?? true);
      setApplyGstOnDelivery(inv.applyGstOnDelivery ?? false);
      setCurrentLines((inv.items || []).map((i: any) => ({
        itemName: i.itemName,
        quantity: i.quantity,
        pricePerUnit: i.pricePerUnit,
        quantityUnit: '',
        total: i.quantity * i.pricePerUnit * (1 - (i.discount || 0) / 100),
        discount: i.discount || 0
      })));
      setListDialogOpen(false);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load invoice', severity: 'error' });
    }
  };

  const handleNewInvoice = () => {
    setEditingInvoiceId(null);
    setCurrentLines([]);
    setPaymentMethod('');
    setOrderType('');
    setCustomerName('');
    setContact('');
    setMobileNo('');
    setNtnNo('');
    setCompany('');
    setCompanyAddress('');
    setCompanyNtnNo('');
    setPaymentDueDate('');
    setInvoiceDate('');
    setDeliveryCharges(0);
    setApplyGst(false);
    setApplyGstOnDelivery(false);
    setSelectedItem({ name: '', pricePerUnit: 0, quantityUnit: '', category: '', addAmountToBillInPOS: false, manufacturingPricePerUnit: null, avgWeightPerUnit: null });
    setQuantity(1);
  };

  const formatPkr = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const exportInvoiceToPdf = async (id: number) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/invoices/${id}`, { headers: { Authorization: token } });
      const inv = res.data;
      const doc = new jsPDF();
      const leftMargin = 14;
      const contentWidth = 180;
      const logoWidth = contentWidth * 0.8;
      const logoHeight = (logoWidth * 40) / 60;
      const logoX = (contentWidth - logoWidth) / 2 + leftMargin;
      doc.addImage('/SAWLOGO.png', 'PNG', logoX, 8, logoWidth, logoHeight);

      let y = logoHeight + 20;

      const isDelivered = inv.status === 'DELIVERED';
      const applyingGst = inv.applyGst !== false;
      const docTypeTitle = isDelivered
        ? (applyingGst ? 'SALES TAX INVOICE' : 'INVOICE')
        : 'QUOTATION';
      const docNumberLabel = isDelivered ? 'Invoice #:' : 'Quotation #:';

      doc.setFontSize(16);
      doc.text(docTypeTitle, leftMargin, y);
      y += 12;

      doc.setFontSize(10);
      const colWidth = 85;
      const rightColX = leftMargin + colWidth + 8;
      const leftLines = [
        `${docNumberLabel} ${inv.id}`,
        `Date: ${inv.createdDate || '-'}`,
        `Payment Method: ${inv.paymentMethod || '-'}`,
        `Order Type: ${inv.orderType || '-'}`,
        `Customer Name: ${inv.customerName || '-'}`,
        `Contact: ${inv.contact || '-'}`,
        `Mobile No: ${inv.mobileNo || '-'}`,
        `NTN No: ${inv.ntnNo || '-'}`
      ];
      const rightLines = [
        `Company: ${inv.company || '-'}`,
        `Company Address: ${inv.companyAddress || '-'}`,
        `Company NTN No: ${inv.companyNtnNo || '-'}`,
        `Payment Due Date: ${inv.paymentDueDate || '-'}`,
        inv.shopName ? `Shop: ${inv.shopName}` : '',
        `Created by: ${inv.username || '-'}`,
        `Status: ${inv.status || '-'}`
      ];
      const numRows = Math.max(leftLines.length, rightLines.length);
      for (let i = 0; i < numRows; i++) {
        const leftText = leftLines[i] || '';
        const rightText = rightLines[i] || '';
        if (leftText) doc.text(leftText, leftMargin, y);
        if (rightText) doc.text(rightText, rightColX, y);
        y += 6;
      }
      y += 6;

      const tableData = (inv.items || []).map((i: any) => {
        const qty = i.quantity ?? 0;
        const price = i.pricePerUnit ?? 0;
        const pct = i.discount ?? 0;
        const total = qty * price * (1 - pct / 100);
        const discountAmount = qty * price * (pct / 100);
        const unit = (i.quantityUnit || '').toUpperCase();
        const quantityDisplay = unit === 'GRAM'
          ? `${(Math.round((qty / 1000) * 100) / 100).toFixed(2)} kg(s)`
          : String(qty);
        const priceDisplay = unit === 'GRAM'
          ? formatPkr(price * 1000)
          : formatPkr(price);
        return [i.itemName, quantityDisplay, priceDisplay, (pct).toFixed(0) + '%', formatPkr(discountAmount), formatPkr(total)];
      });
      autoTable(doc, {
        startY: y,
        head: [['Item', 'Quantity', 'Price/Unit (PKR)', 'Discount %', 'Discount Amount', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [100, 100, 100] }
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      const subtotal = (inv.items || []).reduce((s: number, i: any) => s + (i.quantity ?? 0) * (i.pricePerUnit ?? 0) * (1 - (i.discount ?? 0) / 100), 0);
      doc.text(`Subtotal: ${formatPkr(subtotal)}`, leftMargin, y);
      y += 6;
      doc.text(`Delivery Charges: ${formatPkr(inv.deliveryCharges ?? 0)}`, leftMargin, y);
      y += 6;
      if (applyingGst) {
        doc.text(`GST (18%): ${formatPkr(inv.gstAmount ?? 0)}`, leftMargin, y);
        y += 6;
      }
      const grandTotal = subtotal + (inv.deliveryCharges ?? 0) + (applyingGst ? (inv.gstAmount ?? 0) : 0);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total: ${formatPkr(grandTotal)}`, leftMargin, y);
      y += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('This is a computer generated document and does not need to be signed or stamped.', leftMargin, y);

      doc.save(`invoice-${inv.id}.pdf`);
      setSnackbar({ open: true, message: 'Invoice PDF downloaded', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to export PDF', severity: 'error' });
    }
  };

  const handleSaveInvoice = async () => {
    if (currentLines.length === 0 || !paymentMethod || !orderType) return;
    const body = {
      items: currentLines.map(item => ({
        itemName: item.itemName,
        quantity: item.quantity,
        pricePerUnit: item.pricePerUnit,
        discount: item.discount
      })),
      paymentMethod,
      orderType,
      customerName,
      contact,
      mobileNo,
      ntnNo,
      company,
      companyAddress,
      companyNtnNo,
      paymentDueDate,
      deliveryCharges,
      applyGst,
      applyGstOnDelivery: applyGst ? applyGstOnDelivery : false
    };
    setIsSubmitting(true);
    try {
      if (editingInvoiceId == null) {
        await axios.post(`${API_BASE_URL}/invoices`, body, { headers: { Authorization: token, 'Content-Type': 'application/json' } });
        setSnackbar({ open: true, message: 'Invoice created', severity: 'success' });
      } else {
        await axios.put(`${API_BASE_URL}/invoices/${editingInvoiceId}`, body, { headers: { Authorization: token, 'Content-Type': 'application/json' } });
        setSnackbar({ open: true, message: 'Invoice updated', severity: 'success' });
      }
      handleNewInvoice();
    } catch (e: any) {
      const msg = e.response?.data?.message || (typeof e.response?.data === 'string' ? e.response.data : 'Failed to save invoice');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Add Items</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={handleNewInvoice}>New Invoice</Button>
              <Button variant="outlined" onClick={openListDialog}>Edit existing</Button>
            </Box>
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedItem.category || ''}
              onChange={(e) => {
                const category = e.target.value;
                setSelectedItem({ name: '', pricePerUnit: 0, quantityUnit: '', category, addAmountToBillInPOS: false, manufacturingPricePerUnit: null, avgWeightPerUnit: null });
              }}
              label="Category"
            >
              {getCategoryOptions().map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Item</InputLabel>
              <Select
                value={selectedItem.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const item = items.find(i => i.name === name);
                  if (item) {
                    setSelectedItem({
                      name: item.name,
                      pricePerUnit: item.pricePerUnit || 0,
                      quantityUnit: item.quantityUnit,
                      category: item.category,
                      addAmountToBillInPOS: item.addAmountToBillInPOS,
                      manufacturingPricePerUnit: item.manufacturingPricePerUnit,
                      avgWeightPerUnit: item.avgWeightPerUnit || 0
                    });
                    setQuantity(1);
                    setPieceCount(1);
                  }
                }}
                label="Item"
                disabled={!selectedItem.category}
              >
                {items
                  .filter(item => item.category === selectedItem.category)
                  .map((item) => (
                    <MenuItem key={item.id} value={item.name}>
                      {item.name} (PKR{item.pricePerUnit?.toFixed(2) || 'N/A'}/{item.quantityUnit})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {selectedItem.quantityUnit === 'GRAM' && (
              <TextField
                label="Piece Count"
                type="number"
                value={pieceCount}
                onChange={(e) => {
                  const pc = parseInt(e.target.value) || 0;
                  setPieceCount(pc);
                  if (selectedItem.avgWeightPerUnit) setQuantity(pc * selectedItem.avgWeightPerUnit);
                }}
                InputProps={{ inputProps: { min: 1 } }}
                sx={{ width: 150 }}
              />
            )}
            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(0, parseInt(e.target.value) || 0))}
              InputProps={{ inputProps: { min: 1 } }}
              sx={{ width: 150 }}
            />
            <Button variant="contained" onClick={handleAddToInvoice} disabled={!selectedItem.name || quantity <= 0}>
              Add to Invoice
            </Button>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {editingInvoiceId != null ? `Edit Invoice #${editingInvoiceId}` : 'Current Invoice'}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price/Unit</TableCell>
                  <TableCell align="right">Discount (%)</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentLines.map((line, index) => (
                  <TableRow key={index}>
                    <TableCell>{line.itemName}</TableCell>
                    <TableCell align="right">{line.quantity}</TableCell>
                    <TableCell align="right">PKR{line.pricePerUnit.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={line.discount}
                        onChange={(e) => handleLineDiscountChange(index, parseFloat(e.target.value) || 0)}
                        InputProps={{ inputProps: { min: 0, max: 50, step: 1 } }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="right">PKR{line.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Button variant="outlined" color="error" size="small" onClick={() => handleRemoveFromInvoice(index)}>Remove</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} align="right"><Typography variant="subtitle1" fontWeight="bold">Subtotal:</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle1" fontWeight="bold">PKR{calculateSubtotal().toFixed(2)}</Typography></TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <FormControlLabel
                      control={<Checkbox checked={applyGst} onChange={(e) => { setApplyGst(e.target.checked); if (!e.target.checked) setApplyGstOnDelivery(false); }} />}
                      label="Apply GST"
                    />
                  </TableCell>
                  <TableCell align="right" />
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        label="Delivery Charges"
                        type="number"
                        value={deliveryCharges}
                        onChange={(e) => setDeliveryCharges(Math.max(0, parseFloat(e.target.value) || 0))}
                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        sx={{ width: 150 }}
                      />
                      {applyGst && (
                        <FormControlLabel
                          control={<Checkbox checked={applyGstOnDelivery} onChange={(e) => setApplyGstOnDelivery(e.target.checked)} />}
                          label="Apply GST on Delivery"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">PKR{deliveryCharges.toFixed(2)}</TableCell>
                  <TableCell />
                </TableRow>
                {applyGst && (
                  <TableRow>
                    <TableCell colSpan={4} align="right"><Typography variant="subtitle1">GST (18%):</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1">
                        PKR{((calculateSubtotal() + (applyGstOnDelivery ? deliveryCharges : 0)) * 0.18).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={4} align="right"><Typography variant="subtitle1" fontWeight="bold">Total:</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" fontWeight="bold">
                      PKR{(
                        calculateSubtotal() + deliveryCharges +
                        (applyGst ? (calculateSubtotal() + (applyGstOnDelivery ? deliveryCharges : 0)) * 0.18 : 0)
                      ).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                value={paymentMethod ? paymentMethodOptions.find(o => o.value === paymentMethod) : null}
                onChange={(_, v) => setPaymentMethod(v?.value || '')}
                options={paymentMethodOptions}
                getOptionLabel={(o) => o.label}
                renderInput={(params) => <TextField {...params} label="Payment Method" required />}
                fullWidth
              />
              <Autocomplete
                value={orderType ? orderTypeOptions.find(o => o.value === orderType) : null}
                onChange={(_, v) => setOrderType(v?.value || '')}
                options={orderTypeOptions}
                getOptionLabel={(o) => o.label}
                renderInput={(params) => <TextField {...params} label="Order Type" required />}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <TextField label="Customer Name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} fullWidth size="small" />
              <TextField label="Contact" value={contact} onChange={(e) => setContact(e.target.value)} fullWidth size="small" />
              <TextField label="Mobile No" value={mobileNo} onChange={(e) => setMobileNo(e.target.value)} fullWidth size="small" />
              <TextField label="NTN No" value={ntnNo} onChange={(e) => setNtnNo(e.target.value)} fullWidth size="small" />
              <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} fullWidth size="small" />
              <TextField label="Company Address" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} fullWidth size="small" multiline minRows={2} />
              <TextField label="Company NTN No" value={companyNtnNo} onChange={(e) => setCompanyNtnNo(e.target.value)} fullWidth size="small" />
              <TextField label="Date of Invoice" value={invoiceDate || 'Auto-generated on save'} InputProps={{ readOnly: true }} fullWidth size="small" />
              <TextField label="Payment Due Date" value={paymentDueDate} onChange={(e) => setPaymentDueDate(e.target.value)} type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveInvoice}
                disabled={currentLines.length === 0 || !paymentMethod || !orderType || isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} /> : editingInvoiceId != null ? 'Update Invoice' : 'Create Invoice'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Dialog open={listDialogOpen} onClose={() => setListDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Invoices</DialogTitle>
        <DialogContent>
          {listLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 'calc(100vh - 220px)',
                overflow: 'auto',
                '& .MuiTableCell-root': {
                  padding: '8px 16px',
                  fontSize: '0.875rem'
                }
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 70 }}>Invoice #</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Created</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Shop</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>User</TableCell>
                    <TableCell sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 340, whiteSpace: 'nowrap' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No invoices found
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoiceList.map((inv) => (
                      <TableRow key={inv.id} hover>
                        <TableCell>{inv.id}</TableCell>
                        <TableCell>{inv.createdDate}</TableCell>
                        <TableCell>{inv.shopName || '—'}</TableCell>
                        <TableCell>{inv.username}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{inv.status.replace(/_/g, ' ')}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'inline-flex', flexWrap: 'nowrap', gap: 0.5, alignItems: 'center' }}>
                            <Button variant="outlined" size="small" onClick={() => exportInvoiceToPdf(inv.id)} sx={{ minWidth: 56 }}>PDF</Button>
                            {inv.status === 'PENDING_APPROVAL' && (
                              <>
                                {isAdministrator && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleMarkApproved(inv.id)}
                                    disabled={statusUpdatingId === inv.id}
                                    sx={{ minWidth: 64 }}
                                  >
                                    {statusUpdatingId === inv.id ? <CircularProgress size={14} /> : 'Approve'}
                                  </Button>
                                )}
                                <Button variant="outlined" size="small" onClick={() => handleEditInvoice(inv.id)} sx={{ minWidth: 48 }}>Edit</Button>
                                <Button variant="contained" color="error" size="small" onClick={() => handleDeleteInvoice(inv.id)} disabled={deleteInProgressId === inv.id} sx={{ minWidth: 48 }}>
                                  {deleteInProgressId === inv.id ? <CircularProgress size={14} /> : 'Del'}
                                </Button>
                              </>
                            )}
                            {inv.status === 'APPROVED' && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleMarkDelivered(inv.id)}
                                disabled={statusUpdatingId === inv.id}
                                sx={{ minWidth: 64 }}
                              >
                                {statusUpdatingId === inv.id ? <CircularProgress size={14} /> : 'Deliver'}
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Invoices;
